import { useLocation } from 'preact-iso';
import { useMemo, useState } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { Button } from '../components/ui/Button';
import { CreatePlantModal } from '../components/CreatePlantModal';
import { PlantEditModal } from '../components/PlantEditModal';
import { TriggerRolloutFromPlantsModal } from '../components/TriggerRolloutFromPlantsModal';
import {
  DeviceKindIcon,
  OsIcon,
  type DeviceKind,
  type OsKind,
} from '../components/DeviceIcons';
import { useAsync } from '../hooks/useAsync';
import { api } from '../api/client';
import type {
  ListPlantsOverviewResponse,
  PlantOverviewClientDto,
  PlantOverviewDto,
  PlantUpdateStatus,
} from '../api/types';
import { sessionSignal } from '../auth/session';
import { IconCreate, IconEdit, IconUpdate } from '../icons/HvacIcons';
import './PlantsPage.css';
import '../components/DeviceIcons.css';

interface ScopesResponse {
  scopes: Array<{ id: string; name: string; description: string | null }>;
}

type HmiFilter = 'any' | 'yes' | 'no';
type StatusFilter = 'any' | PlantUpdateStatus;

/**
 * Anlagen-Übersicht (rewritten 2026-08-29): richer than the old plain
 * name/address/id list. Each row now shows the newest client (OS + chassis),
 * a last-seen indicator, an aggregate update-status badge, and a "Update..."
 * button that opens the rollout trigger dialog.
 *
 * Data lives on the server: /api/plants/overview returns paginated,
 * search-and-filter-aware rows. Client-side we only render the current page.
 * When paging is triggered we refetch with new params, so the response cost
 * stays proportional to page size, not total fleet size.
 */
export function PlantsPage() {
  const location = useLocation();
  const canAdmin = sessionSignal.value.user?.permissions.includes('plants.admin') ?? false;

  // Controlled query params. `pageSize` is fixed at 50 for now — we render a
  // simple prev/next pager. A pageSize selector is an easy follow-up but
  // adds noise to the toolbar that early operators do not need.
  const [search, setSearch] = useState('');
  const [hasHmi, setHasHmi] = useState<HmiFilter>('any');
  const [status, setStatus] = useState<StatusFilter>('any');
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [refreshTick, setRefreshTick] = useState(0);

  // Modal orchestration state.
  const [showCreate, setShowCreate] = useState(false);
  const [scopes, setScopes] = useState<Array<{ id: string; name: string }>>([]);
  const [editTarget, setEditTarget] = useState<PlantOverviewDto | null>(null);
  const [rolloutTarget, setRolloutTarget] = useState<PlantOverviewDto | null>(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    if (hasHmi !== 'any') params.set('hasHmi', hasHmi === 'yes' ? 'true' : 'false');
    if (status !== 'any') params.set('updateStatus', status);
    params.set('page', page.toString());
    params.set('pageSize', pageSize.toString());
    return params.toString();
  }, [search, hasHmi, status, page]);

  const state = useAsync(
    (signal) => api.get<ListPlantsOverviewResponse>(`/api/plants/overview?${queryString}`, signal),
    [queryString, refreshTick],
  );

  // Seit 13.10.2026 legt der Server Anlagen nur im aktiven DataScope an
  // (CreatePlantHandler). Die Liste aller Scopes liefert hier nur noch den
  // Namen dazu; zur Auswahl steht allein der aktive.
  const openCreate = async () => {
    setShowCreate(true);
    const activeId = sessionSignal.value.user?.activeDataScope;
    if (!activeId) { setScopes([]); return; }
    let name = 'Aktueller Data-Scope';
    try {
      const res = await api.get<ScopesResponse>('/api/identity/scopes');
      name = res.scopes.find((s) => s.id === activeId)?.name ?? name;
    } catch {
      // Tenant Admins ohne users.admin/groups.admin duerfen die Liste nicht
      // lesen; dann bleibt es beim neutralen Namen.
    }
    setScopes([{ id: activeId, name }]);
  };

  const actions = canAdmin ? (
    <Button variant="primary" size="sm" onClick={openCreate}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCreate size={14} />Neue Anlage</span>
    </Button>
  ) : undefined;

  const refresh = () => setRefreshTick((n) => n + 1);

  const totalPages = state.status === 'ok'
    ? Math.max(1, Math.ceil(state.data.total / pageSize))
    : 1;

  return (
    <AppShell title="Anlagen" actions={actions}>
      <div class="hv-plants">
        <div class="hv-plants__toolbar">
          <div class="hv-plants__search">
            <input
              type="search"
              placeholder="Suche in Name, Adresse, Kontakt..."
              value={search}
              onInput={(e) => {
                setPage(1);
                setSearch((e.currentTarget as HTMLInputElement).value);
              }}
            />
          </div>
          <label class="hv-plants__filter">
            <span>HMI</span>
            <select value={hasHmi} onChange={(e) => {
              setPage(1);
              setHasHmi((e.currentTarget as HTMLSelectElement).value as HmiFilter);
            }}>
              <option value="any">alle</option>
              <option value="yes">vorhanden</option>
              <option value="no">fehlt</option>
            </select>
          </label>
          <label class="hv-plants__filter">
            <span>Update-Status</span>
            <select value={status} onChange={(e) => {
              setPage(1);
              setStatus((e.currentTarget as HTMLSelectElement).value as StatusFilter);
            }}>
              <option value="any">alle</option>
              <option value="current">aktuell</option>
              <option value="in_progress">läuft</option>
              <option value="error">Fehler</option>
              <option value="unknown">kein Client</option>
            </select>
          </label>
          <div class="hv-plants__toolbar-info">
            {state.status === 'ok'
              ? `${state.data.total} Anlage(n)`
              : state.status === 'loading' ? 'lade...' : 'Fehler'}
          </div>
        </div>

        {state.status === 'loading' && (
          <div class="hv-plants__empty">Loading...</div>
        )}
        {state.status === 'error' && (
          <div class="hv-plants__empty">Konnte Anlagen nicht laden.</div>
        )}
        {state.status === 'ok' && (
          <>
            {state.data.plants.length === 0 ? (
              <div class="hv-plants__empty">Keine Anlagen für diese Filter.</div>
            ) : (
              <table class="hv-plants__table">
                <thead>
                  <tr>
                    {/* Two leading glyph columns for at-a-glance identification
                        of hardware kind (panel vs. gateway) and OS. Aria-labels
                        carry the semantic text; column headers stay small icons
                        to keep the row compact. */}
                    <th class="hv-plants__th-icon" aria-label="Geraetetyp">HW</th>
                    <th class="hv-plants__th-icon" aria-label="OS">OS</th>
                    <th class="hv-plants__th-icon" aria-label="Auf Karte anzeigen">Karte</th>
                    <th>Name / Kontakt</th>
                    <th>Anschrift</th>
                    <th>Zuletzt gesehen</th>
                    <th>Client</th>
                    <th>Update-Status</th>
                    <th>HMI</th>
                    <th style={{ textAlign: 'right' }}>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {state.data.plants.map((p) => (
                    <PlantRow
                      key={p.id}
                      plant={p}
                      canAdmin={canAdmin}
                      onRowClick={() => location.route(`/plants/${p.id}`)}
                      onEdit={() => setEditTarget(p)}
                      onRollout={() => setRolloutTarget(p)}
                      onOpenMap={() => location.route(`/?plantId=${encodeURIComponent(p.id)}`)}
                    />
                  ))}
                </tbody>
              </table>
            )}

            <div class="hv-plants__pager">
              <div class="hv-plants__pager-info">
                Seite {state.data.page} von {totalPages} (Seitengröße {pageSize})
              </div>
              <button
                type="button"
                class="hv-btn"
                disabled={state.data.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >Vorherige</button>
              <button
                type="button"
                class="hv-btn"
                disabled={state.data.page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >Nächste</button>
            </div>
          </>
        )}
      </div>

      {showCreate && (
        <CreatePlantModal
          scopes={scopes}
          onCreated={(p) => {
            setShowCreate(false);
            location.route(`/plants/${p.id}`);
          }}
          onClose={() => setShowCreate(false)}
        />
      )}
      {editTarget && (
        <PlantEditModal
          plant={editTarget}
          onSaved={() => {
            setEditTarget(null);
            refresh();
          }}
          onDeleted={() => {
            setEditTarget(null);
            refresh();
          }}
          onClose={() => setEditTarget(null)}
        />
      )}
      {rolloutTarget && (
        <TriggerRolloutFromPlantsModal
          plant={rolloutTarget}
          onClose={() => {
            setRolloutTarget(null);
            refresh();
          }}
        />
      )}
    </AppShell>
  );
}

// --- Row rendering --------------------------------------------------------

function PlantRow({
  plant,
  canAdmin,
  onRowClick,
  onEdit,
  onRollout,
  onOpenMap,
}: {
  plant: PlantOverviewDto;
  canAdmin: boolean;
  onRowClick(): void;
  onEdit(): void;
  onOpenMap(): void;
  onRollout(): void;
}) {
  const addressBlock = renderAddress(plant);
  const lastSeenLabel = renderLastSeen(plant.lastSeenAt);
  const clientLabel = renderClient(plant);
  const statusBadge = renderStatus(plant.updateStatus);
  const hmiBadge = plant.hasHmi
    ? <span class="hv-plants__badge hv-plants__badge--ok">HMI</span>
    : <span class="hv-plants__badge hv-plants__badge--muted">keine</span>;

  const stop = (e: Event) => e.stopPropagation();
  const hasClients = plant.clients.length > 0;
  // Katalog gewinnt: sobald ein Product zugewiesen ist, kommen HW + OS
  // aus dem Katalog. Sonst die alte Heuristik aus runtime-osKind + Freitext-
  // hardwareKind. Product-osKind/deviceKind sind Wire-Strings ("linux",
  // "gateway", …); wir mappen sie auf die schmaleren DeviceKind/OsKind-
  // Unions der Icon-Komponenten.
  const primary = plant.primaryClient;
  const productDeviceKind = mapProductDeviceKind(primary?.productDeviceKind);
  const productOsKind = mapProductOsKind(primary?.productOsKind);
  const deviceKind = productDeviceKind ?? inferDeviceKind(plant);
  const osKind = productOsKind ?? ((primary?.osKind as OsKind) ?? null);

  return (
    <tr class="hv-plants__row-clickable" onClick={onRowClick}>
      <td class="hv-plants__td-icon"><DeviceKindIcon kind={deviceKind} /></td>
      <td class="hv-plants__td-icon"><OsIcon os={osKind} /></td>
      <td class="hv-plants__td-icon">
        {plant.latitude !== null && plant.longitude !== null && (
          <button type="button"
                  class="hv-plants__map-jump"
                  title={`Auf Karte anzeigen: ${plant.displayName}`}
                  aria-label={`${plant.displayName} auf Karte anzeigen`}
                  onClick={(e) => { e.stopPropagation(); onOpenMap(); }}>
            <GlobeIcon />
          </button>
        )}
      </td>
      <td>
        <div class="hv-plants__name">
          {plant.displayName}
          {plant.contactName && (
            <span class="hv-plants__name-contact">{plant.contactName}</span>
          )}
        </div>
      </td>
      <td>
        <div class="hv-plants__address">{addressBlock}</div>
      </td>
      <td>{lastSeenLabel}</td>
      <td>{clientLabel}</td>
      <td>{statusBadge}</td>
      <td>{hmiBadge}</td>
      <td>
        <div class="hv-plants__actions" onClick={stop}>
          {canAdmin && (
            <button type="button" class="hv-btn hv-btn--sm" onClick={onEdit}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconEdit size={14} />Bearbeiten</span>
            </button>
          )}
          {canAdmin && (
            <button
              type="button"
              class="hv-btn hv-btn--sm hv-btn--primary"
              onClick={onRollout}
              disabled={!hasClients}
              title={hasClients ? 'Rollout auf einen Client der Anlage auslösen' : 'Kein Client vorhanden'}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconUpdate size={14} />Update...</span>
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// --- Renderers ------------------------------------------------------------

function renderAddress(plant: PlantOverviewDto) {
  // Prefer the structured block when at least street or zip+city is set.
  const hasStructured = plant.addressStreet || plant.addressCity || plant.addressZip;
  if (hasStructured) {
    const line2 = [plant.addressZip, plant.addressCity].filter(Boolean).join(' ');
    const line3 = plant.addressCountry ?? '';
    return (
      <>
        {plant.addressStreet && <span class="hv-plants__address-line">{plant.addressStreet}</span>}
        {line2 && <span class="hv-plants__address-line">{line2}</span>}
        {line3 && <span class="hv-plants__address-line">{line3}</span>}
      </>
    );
  }
  if (plant.address) {
    return <span class="hv-plants__address-line">{plant.address}</span>;
  }
  return <span class="hv-plants__address-line" style={{ color: 'var(--text-muted)' }}>keine Anschrift</span>;
}

function renderLastSeen(iso: string | null) {
  if (!iso) return <span class="hv-plants__lastseen hv-plants__lastseen--absent">nie</span>;
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const hours = diff / (60 * 60 * 1000);
  const label = formatRelative(iso);
  // Older than 24h -> warn tint. Kept intentionally simple; a future update
  // could compute this against a per-tenant stale threshold that the server
  // already exposes for /api/clients.
  const cls = hours > 24
    ? 'hv-plants__lastseen hv-plants__lastseen--stale'
    : 'hv-plants__lastseen';
  return (
    <span class={cls} title={new Date(iso).toLocaleString()}>
      vor {label}
    </span>
  );
}

function renderClient(plant: PlantOverviewDto) {
  const c = plant.primaryClient;
  if (!c) {
    return <span class="hv-plants__lastseen hv-plants__lastseen--absent">kein Client</span>;
  }
  // Preference order (2026-08-31, product-catalog rollout):
  //   1. productDisplayName  — kuratierter Katalog-Name
  //   2. chassisDisplayName  — auto-erkannt vom Runtime-Info
  //   3. clientTypeFallback  — vom osKind abgeleitet
  // hardwareKind (Legacy-Freitext) fällt raus — sobald Produkte gepflegt sind
  // ist der frei-getippte hardwareKind kein Signal mehr.
  const typeLabel = c.productDisplayName
    ?? c.chassisDisplayName
    ?? clientTypeFallback(c.osKind);
  // Meta-Zeile: Form-Faktor · OS aus Katalog (wenn zugewiesen), sonst
  // Runtime-osKind, sonst Fallback "Typ unbekannt".
  const metaParts: string[] = [];
  const formLabel = formatDeviceKind(c.productDeviceKind);
  const osLabelResolved = formatOsKind(c.productOsKind ?? c.osKind);
  if (formLabel) metaParts.push(formLabel);
  if (osLabelResolved) metaParts.push(osLabelResolved);
  if (metaParts.length === 0) metaParts.push('Typ unbekannt');
  if (plant.clientCount > 1) metaParts.push(`+${plant.clientCount - 1} weitere`);
  const shortSerial = c.serialNumber && c.serialNumber.length > 10
    ? `${c.serialNumber.slice(0, 10)}…`
    : c.serialNumber;
  return (
    <div class="hv-plants__client">
      <span class="hv-plants__client-chassis">{typeLabel}</span>
      <span class="hv-plants__client-meta">{metaParts.join(' · ')}</span>
      {shortSerial && (
        <span class="hv-plants__client-meta mono" style={{ letterSpacing: '0.02em' }}>
          S/N {shortSerial}
        </span>
      )}
      {renderWarrantyBadge(c.warrantyStatus)}
    </div>
  );
}

function renderWarrantyBadge(status: PlantOverviewClientDto['warrantyStatus']) {
  if (!status) return null;
  switch (status) {
    case 'ok':
      return <span class="hv-plants__badge hv-plants__badge--ok">Garantie ✓</span>;
    case 'expiring':
      return <span class="hv-plants__badge hv-plants__badge--warn">Garantie ⚠</span>;
    case 'expired':
      return <span class="hv-plants__badge hv-plants__badge--error">Garantie ✕</span>;
    default:
      return null;
  }
}

/** Katalog-DeviceKind auf einen menschenlesbaren Formfaktor-Label mappen.
 *  Null wenn unbekannt (Meta-Zeile faellt dann auf OS/Fallback zurueck). */
function formatDeviceKind(v: string | null | undefined): string | null {
  switch (v) {
    case 'gateway': return 'Gateway';
    case 'panel': return 'Touchpanel';
    case 'pc': return 'PC';
    case 'embedded': return 'Embedded';
    default: return null;
  }
}

/** Runtime- oder Katalog-OsKind auf einen Klartext-Label mappen. */
function formatOsKind(v: string | null | undefined): string | null {
  switch (v) {
    case 'linux': return 'Linux';
    case 'android': return 'Android';
    case 'windows': return 'Windows';
    case 'ios': return 'iOS';
    case 'embedded': return 'Embedded';
    default: return null;
  }
}

function clientTypeFallback(osKind: string | null | undefined): string {
  switch (osKind) {
    case 'linux': return 'Linux-Gateway';
    case 'android': return 'Android-Panel';
    case 'windows': return 'Windows-PC';
    default: return 'Client (Typ unbekannt)';
  }
}

/** Kompaktes Globe-Icon (16px, currentColor). Wird nur bei Plants mit
 *  geo-Position gerendert und wechselt bei Klick auf die Map-Ansicht
 *  mit Focus auf die Anlage. */
function GlobeIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" stroke-width="1.6"
         stroke-linecap="round" stroke-linejoin="round"
         aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.6 2.9 4 6 4 9s-1.4 6.1-4 9c-2.6-2.9-4-6-4-9s1.4-6.1 4-9z" />
    </svg>
  );
}

/**
 * Same heuristic the server uses for the map: hardware-kind keyword wins,
 * otherwise the OS decides. Only kept in the frontend for the plants list —
 * the map DTO carries the server-computed answer.
 */
function inferDeviceKind(plant: PlantOverviewDto): DeviceKind {
  const hw = plant.primaryClient?.hardwareKind?.toLowerCase() ?? '';
  if (hw.includes('panel') || hw.includes('touch')) return 'panel';
  if (hw.includes('unipi') || hw.includes('revpi') || hw.includes('gateway')) return 'gateway';
  switch (plant.primaryClient?.osKind) {
    case 'android': return 'panel';
    case 'linux': case 'windows': return 'gateway';
    default: return null;
  }
}

/** Product-DeviceKind ("gateway"|"panel"|"pc"|"embedded"|"unknown"|null)
 *  auf die DeviceKindIcon-Union mappen. `pc` faellt auf `gateway`, weil
 *  das aktuelle Icon-Set beides gleich rendert — Umbau kommt mit einem
 *  eigenen PC-Icon spaeter. */
function mapProductDeviceKind(v: string | null | undefined): DeviceKind {
  switch (v) {
    case 'gateway': return 'gateway';
    case 'panel': return 'panel';
    case 'pc': return 'gateway';
    default: return null;
  }
}

/** Product-OsKind auf die OsIcon-Union mappen. `ios` + `embedded` haben
 *  noch keine eigenen Icons — bewusst null zurueckgeben, damit die
 *  Zelle leer bleibt statt einen falschen Icon-Fallback zu zeigen. */
function mapProductOsKind(v: string | null | undefined): OsKind {
  switch (v) {
    case 'linux': return 'linux';
    case 'android': return 'android';
    case 'windows': return 'windows';
    default: return null;
  }
}

function renderStatus(status: PlantUpdateStatus) {
  switch (status) {
    case 'current':
      return <span class="hv-plants__badge hv-plants__badge--ok">aktuell</span>;
    case 'in_progress':
      return <span class="hv-plants__badge hv-plants__badge--warn">läuft</span>;
    case 'error':
      return <span class="hv-plants__badge hv-plants__badge--error">Fehler</span>;
    case 'unknown':
    default:
      return <span class="hv-plants__badge hv-plants__badge--muted">–</span>;
  }
}

function formatRelative(iso: string): string {
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diff = Math.max(0, now - t);
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec} s`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} h`;
  const day = Math.round(hr / 24);
  return `${day} Tage`;
}
